import { ClickAwayListener } from "@mui/material";
import { useHandler } from "@redotech/react-util/hook";
import type { MacroVariable } from "@redotech/redo-support-common/macro";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { BaseRedoInput } from "@redotech/redo-web/arbiter-components/input/base-redo-text-input";
import { Dropdown } from "@redotech/redo-web/dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useMemo, useRef, useState } from "react";
import { insertVariableToken } from "../hooks/use-variable-autocomplete";
import type { UseMessageEditorResult } from "./message-editor/use-message-editor";
import * as variableAutocompleteCss from "./variable-autocomplete.module.css";
import { filterMacroVariables, VariableList } from "./variable-list";

export interface VariableInsertButtonProps {
  editorState: UseMessageEditorResult;
  /** When true, hide both the button menu and prevent it from opening.
   *  Used while the slash autocomplete is already open at the caret to
   *  prevent two stacked menus on screen. */
  disabled?: boolean;
}

export const VariableInsertButton = memo(function VariableInsertButton({
  editorState,
  disabled = false,
}: VariableInsertButtonProps) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");
  const [focusedIndex, setFocusedIndex] = useState<number | undefined>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredVariables = useMemo(
    () => filterMacroVariables(search),
    [search],
  );

  const close = useHandler(() => {
    setOpen(false);
    setSearch("");
    setFocusedIndex(0);
  });

  const handleSelect = useHandler((variable: MacroVariable) => {
    insertVariableToken(editorState, variable);
    close();
  });

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Flex dir="row">
        <Flex ref={setAnchor}>
          <Tooltip title="Or type / in the message to insert a variable.">
            <RedoButton
              disabled={disabled}
              hierarchy="tertiary"
              onClick={() => setOpen((v) => !v)}
              size="sm"
              text="Insert variable"
            />
          </Tooltip>
        </Flex>
        <Dropdown
          anchor={anchor}
          constrainHeight
          fitToAnchor={false}
          flexProps={{ p: "none" }}
          open={open && !disabled}
          placement="bottom-end"
        >
          <Flex
            className={variableAutocompleteCss.dropdownContent}
            dir="column"
            gap="none"
            ref={containerRef}
          >
            <Flex
              borderBottomWidth="1px"
              borderColor="primary"
              borderStyle="solid"
              px="lg"
              py="md"
            >
              <BaseRedoInput
                autoFocus
                placeholder="Search variables..."
                setValue={setSearch}
                size="sm"
                value={search}
              />
            </Flex>
            <VariableList
              focusedIndex={focusedIndex}
              onSelect={handleSelect}
              refToListenTo={containerRef.current}
              setFocusedIndex={setFocusedIndex}
              variables={filteredVariables}
            />
          </Flex>
        </Dropdown>
      </Flex>
    </ClickAwayListener>
  );
});
